import { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Trash2, Search } from "lucide-react";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";
import { SecurityList } from "../components/analysis/SecuritySection";
import { PersonaHeader } from "../components/persona/PersonaSections";

const auth = () => {
  const token = localStorage.getItem("trp_token");
  return { withCredentials: true, headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const CheckForm = ({ onSubmit, loading, label, testid }) => {
  const [url, setUrl] = useState("");
  return (
    <form onSubmit={async (e) => { e.preventDefault(); if (url.trim() && (await onSubmit(url.trim())) !== false) setUrl(""); }} className="flex flex-col sm:flex-row gap-3" data-testid={testid}>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="vasafirma.sk" className="flex-1 px-4 py-3.5 bg-white/5 border border-white/15 rounded-lg text-white placeholder-white/40 focus:outline-none focus:border-[#a855f7]" data-testid={`${testid}-input`} />
      <button disabled={loading} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#a855f7] hover:bg-[#9333ea] text-white rounded-lg font-black uppercase tracking-wider text-sm disabled:opacity-50" data-testid={`${testid}-submit`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} {label}
      </button>
    </form>
  );
};

const Monitors = ({ data, onAdd, onDelete, adding }) => (
  <section className="space-y-5" data-testid="security-monitors">
    <h2 className="text-2xl font-black font-['Cabinet_Grotesk']">Sledované weby ({data.monitors.length}/5)</h2>
    <CheckForm onSubmit={onAdd} loading={adding} label="Pridať web" testid="security-add-form" />
    {data.monitors.map((m) => (
      <div key={m.id} className="rounded-2xl border border-white/10 bg-white/[0.03] p-5" data-testid={`security-monitor-${m.id}`}>
        <div className="flex justify-between items-center mb-3">
          <p className="font-bold break-all">{m.url}</p>
          <button onClick={() => onDelete(m.id)} className="p-2 text-white/50 hover:text-red-400" aria-label="Odstrániť" data-testid={`security-monitor-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button>
        </div>
        {m.latest && <SecurityList security={m.latest} />}
      </div>
    ))}
  </section>
);

export default function SecurityMonitorPage() {
  const { user } = useAuth();
  const [result, setResult] = useState(null);
  const [checking, setChecking] = useState(false);
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try { setData((await axios.get(`${API}/security/monitors`, auth())).data); } catch { setData(null); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const check = async (url) => {
    setChecking(true);
    let ok = true;
    try { setResult((await axios.post(`${API}/security/check`, { url })).data); }
    catch (e) { ok = false; toast.error(e?.response?.data?.detail || "Web sa nepodarilo skontrolovať."); }
    setChecking(false);
    return ok;
  };
  const add = async (url) => {
    setAdding(true);
    let ok = true;
    try { await axios.post(`${API}/security/monitors`, { url }, auth()); await load(); toast.success("Web pridaný do sledovania"); }
    catch (e) { ok = false; toast.error(e?.response?.data?.detail || "Pridanie zlyhalo"); }
    setAdding(false);
    return ok;
  };
  const remove = async (id) => { if (!window.confirm("Odstrániť web zo sledovania?")) return; await axios.delete(`${API}/security/monitors/${id}`, auth()); load(); };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="security-monitor-page">
      <Helmet>
        <title>Bezpečnosť webu – bezplatná kontrola a týždenné sledovanie | TopRankPulse</title>
        <meta name="description" content="Skontrolujte HTTPS, bezpečnostné hlavičky, ochranu emailu (SPF, DMARC), zastarané verzie a cookie lištu. Týždenné sledovanie s upozornením za 19 € mesačne." />
      </Helmet>
      <PersonaHeader />
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-14 space-y-12">
        <section>
          <ShieldCheck className="w-10 h-10 text-[#c084fc] mb-5" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-[1.05]">Je váš web bezpečný a dôveryhodný?</h1>
          <p className="mt-5 text-base md:text-lg text-white/75 max-w-2xl">Hacknutý alebo nezabezpečený web môže vypadnúť z Googlu a AI vyhľadávače ho neodporúčajú. Skontrolujeme HTTPS, bezpečnostné hlavičky, ochranu emailu pred podvrhnutím, zverejnené verzie softvéru a cookie lištu. Kontrola je pasívna a do webu nezasahuje.</p>
          <div className="mt-8"><CheckForm onSubmit={check} loading={checking} label="Skontrolovať zadarmo" testid="security-check-form" /></div>
        </section>
        {result && <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6" data-testid="security-check-result"><SecurityList security={result} /></section>}
        {data?.subscribed && <Monitors data={data} onAdd={add} onDelete={remove} adding={adding} />}
        {!data?.subscribed && (
          <OrderNowBottomCTA packageId="addon_security_monitor" priceEur={19} interval="mes" serviceName="Sledovanie bezpečnosti webu"
            headline="Sledujte bezpečnosť webu každý týždeň" subcopy="Až 5 webov, kontrola raz týždenne a email hneď, keď sa objaví nový problém (napr. končiaci certifikát)." testid="security-final-cta" />
        )}
      </main>
    </div>
  );
}
